/* 每日情報 — 分點調查局（chipk-fendian 每日分點彙整）
 *
 *  呈現理財寶籌碼K線「分點調查局」本機累積資料的每日彙整：高手券商榜（預設
 *  波段常勝軍）、自選券商 Top15、共同動向、大盤籌碼四區。
 *  資料來源：portal 後端 `/api/fendian/*`（代理 Windows 收集端 192.168.11.37:8819
 *  的 `/api/daily*`，上游 JSON 原樣轉回）：
 *    GET /fendian/dates?limit=60    → { ok, ranking, n, rows:[{ date, rank, branches, market, weekday }] } 新→舊
 *    GET /fendian/daily?date=YYYY-MM-DD（省略＝最新一天）
 *      → { ok, date, weekday, generated_at, freshness, rank, branches, consensus, market }
 *  上游語意（chipk-fendian docs/daily-api.md）：
 *    • 每個區塊各自回「≤ 請求日的最近一筆」，帶 date / exact / behind_trading_days。
 *    • 自選券商 Top15 每週一才匯上週五的近 20 日累計 → 平日 exact=false、落後 1～4
 *      個交易日是設計不是錯誤。
 *    • 金額單位：branches / consensus 的 net_amt 是「千元」；market.gov 是「億」。
 *    • polarity inverse（反指標，如國泰-敦南）：買超視為賣訊；consensus.score 已計入。
 *    • 名稱偶有 OCR 勾選框殘字前綴（√ v ~ ^ : .），JSON 照原值回，顯示時剝掉。
 *  錯誤：400/404 帶 { detail }；502 = 收集端（192.168.11.37）離線 → 友善提示 + 重試。
 *
 *  權限：**員工限定**——route key 'fendian' 刻意不在 hr-shell.jsx `_ANALYST_ROUTES` /
 *  `_ANALYST_LITE_ROUTES` / `_NON_STAFF_ROUTES`，受限帳號由 canAccessRoute 擋下
 *  （router 越權導回 #/hub、類別頁卡片不渲染、Cmd+K 不出現）；後端 /api/fendian
 *  亦不在任何非員工白名單。
 *  三態骨架比照 hr-morning-focus.jsx。
 */

/* 台股慣例紅漲綠跌：偏多／名次上升用紅系、偏空／名次下降用綠系（色票取 HR.down / HR.accent） */
/* Render-time function, not a module-level const: HR colours are var(--gx-*) under Expressive
 * and restore() must bring Bento back (SPEC §1.2「內頁換膚」, tests/test_expressive_pages.cjs §7). */
function fdBull() { return { fg: HR.down, bg: '#FAEFEF' }; }
function fdBear() { return { fg: HR.accent, bg: '#E8F1EE' }; }

/* OCR 殘字前綴剝除 — 對齊上游 daily_api._clean：開頭是殘字且下一字是中文才剝，
 * 只影響顯示，資料本身照原值。 */
const FD_JUNK = '√v~^:. ';
function FD_clean(name) {
  let s = name || '';
  while (s.length > 1 && FD_JUNK.indexOf(s[0]) !== -1 && s[1] >= '一' && s[1] <= '鿿') s = s.slice(1);
  return s;
}

/* YYYY-MM-DD → MM/DD（週）；解析失敗退回原字串 */
function FD_dateLabel(iso) {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d)) return iso;
  const pad = (n) => String(n).padStart(2, '0');
  const wd = ['日', '一', '二', '三', '四', '五', '六'][d.getDay()];
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}（${wd}）`;
}

/* 千元 → 人看的億／萬（對齊上游 daily_api._amt），正數補 + 號 */
function FD_amt(k) {
  if (k === null || k === undefined) return '—';
  const v = k * 1000;
  const a = Math.abs(v);
  const sign = v > 0 ? '+' : '';
  if (a >= 1e8) return `${sign}${(v / 1e8).toFixed(1)}億`;
  if (a >= 1e4) return `${sign}${Math.round(v / 1e4)}萬`;
  return `${sign}${Math.round(v)}元`;
}

/* 一般數值：浮點去尾零、整數千分位；signed 補 + 號 */
function FD_num(v, suffix) {
  if (v === null || v === undefined || v === '') return '—';
  const s = typeof v === 'number'
    ? (Number.isInteger(v) ? v.toLocaleString('en-US') : String(Number(v.toFixed(2))))
    : String(v);
  return s + (suffix || '');
}
function FD_signed(v, suffix) {
  if (v === null || v === undefined) return '—';
  return (v > 0 ? '+' : '') + FD_num(v, suffix);
}

/* 502/503/504 一律視為收集端離線 */
function FD_isOffline(err) {
  return !!err && (err.status === 502 || err.status === 503 || err.status === 504);
}

/* 區塊標頭：標題 + 資料日 + 當日／落後狀態（byDesign=true → 週更設計，用中性色不示警） */
function FD_SectionHead({ title, sub, sec, byDesign, right }) {
  const date = sec && sec.date;
  const behind = sec && sec.behind_trading_days;
  let status = null;
  if (!date) status = <Pill tone="neutral">無資料</Pill>;
  else if (sec.exact) status = <Pill tone="accent" dot>當日資料</Pill>;
  else if (byDesign) status = <Pill tone="neutral">{behind ? `週更・較請求日晚 ${behind} 個交易日` : '週更・非請求日資料'}</Pill>;
  else status = <Pill tone="warn" dot>{behind ? `落後 ${behind} 個交易日` : '非請求日資料'}</Pill>;
  return (
    <div style={uiStyle({
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
      padding: '14px 20px', borderBottom: `1px solid ${HR.rule}`, background: HR.surfaceAlt,
    }, { padding: '18px 20px 12px', borderBottom: 'none', background: 'transparent' })}>
      <span style={uiStyle(
        { fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: 600, color: HR.ink },
        { fontSize: GxT.size.h3, fontWeight: GxT.weight.title },
      )}>{title}</span>
      {sub && <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{sub}</span>}
      <span style={{ flex: 1 }} />
      <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkSoft }}>
        資料日 {date ? FD_dateLabel(date) : '—'}
      </span>
      {status}
      {right}
    </div>
  );
}

/* 表格共用樣式 — 一律是函式、render 時才讀 token：Expressive 外殼會在 eval 期 patch
 * FontSizes／HR.inkFaint，模組頂層常數會把 patch 後的值凍住、降級後不還原
 * （plan-expressive-inner-pages.md §2(d)-2）。Bento 回傳值與原物件逐 key 相同。
 * gx-lazy:FD_table_styles:begin（JSX-free；tests/test_gx_p3_pages.cjs 以 vm 擷取） */
function FD_th() {
  return uiStyle({
    textAlign: 'left', padding: '8px 10px', fontSize: FontSizes.trading_lg, fontWeight: 500,
    color: HR.inkFaint, letterSpacing: '0.06em', borderBottom: `1px solid ${HR.rule}`, whiteSpace: 'nowrap',
  }, { fontWeight: GxT.weight.label, letterSpacing: 0, color: GxT.color.onV });
}
function FD_td() {
  return {
    padding: '7px 10px', fontSize: FontSizes.xxs, color: HR.ink,
    borderBottom: `1px solid ${HR.ruleSoft}`, whiteSpace: 'nowrap',
  };
}
function FD_num_td() { return { ...FD_td(), textAlign: 'right', fontFamily: HR.mono }; }
function FD_num_th() { return { ...FD_th(), textAlign: 'right' }; }
/* gx-lazy:FD_table_styles:end */

function FD_Note({ children, tone }) {
  const fg = tone === 'warn' ? HR.warn : tone === 'down' ? HR.down : HR.inkFaint;
  return <div style={{ fontSize: FontSizes.trading_lg, color: fg, lineHeight: 1.6 }}>{children}</div>;
}

/* 「展開全部」切換鈕 */
function FD_MoreToggle({ open, total, shown, onToggle }) {
  if (total <= shown && !open) return null;
  return (
    <button
      type="button" onClick={onToggle}
      style={uiStyle({
        marginTop: 8, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
        border: `1px solid ${HR.ruleSoft}`, background: HR.surface, color: HR.accent,
        fontSize: FontSizes.trading_lg, fontFamily: HR.sans,
      }, {
        minHeight: 32, padding: '0 14px', borderRadius: GxT.radius.btnSm, border: 'none',
        background: GxT.color.pc, color: GxT.color.onPc, fontWeight: GxT.weight.label,
      })}
    >{open ? '收合' : `展開全部 ${total} 筆`}</button>
  );
}

/* ─────────────  資料新鮮度橫幅  ───────────── */
function FD_FreshRow({ label, date, pill, note }) {
  return (
    // flex-wrap：桌面一列三欄，手機窄寬時狀態與說明自動換到下一行
    <div style={{
      display: 'flex', flexWrap: 'wrap', columnGap: 12, rowGap: 6, alignItems: 'center',
      padding: '8px 0', borderBottom: `1px solid ${HR.ruleSoft}`,
    }}>
      <span style={{ width: 130, flexShrink: 0, fontSize: FontSizes.xxs, color: HR.inkSoft, fontWeight: 500 }}>{label}</span>
      <span style={{ width: 120, flexShrink: 0, fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.ink }}>{date ? FD_dateLabel(date) : '—'}</span>
      <span style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        {pill}
        {note && <FD_Note>{note}</FD_Note>}
      </span>
    </div>
  );
}

function FD_FreshnessBanner({ fresh, market }) {
  if (!fresh) return null;
  const rankOk = !!fresh.rank_is_current;
  // 大盤籌碼：落後 ≥2 個交易日視為停滯（落地檔目前停在 9/01，未隨每日更新）
  const mBehind = market && market.behind_trading_days;
  const marketStale = !fresh.market_last_date || (mBehind != null ? mBehind >= 2 : true);
  return (
    <div style={uiStyle({
      background: rankOk ? HR.surface : '#FBF4E8',
      border: `1px solid ${rankOk ? HR.rule : '#E8D3AE'}`,
      borderLeft: `4px solid ${rankOk ? HR.accent : HR.warn}`,
      borderRadius: 6, padding: '12px 20px 6px', marginBottom: 18,
    }, {
      // 無框卡片；未更新時保留淺琥珀底示警，另有 warn Pill（語意不變）
      border: 'none', borderLeft: 'none',
      borderRadius: GxT.radius.card, padding: '16px 20px 8px', marginBottom: 20,
    })}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
        <span style={uiStyle(
          { fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: 600, color: HR.ink },
          { fontSize: GxT.size.h3, fontWeight: GxT.weight.title },
        )}>資料新鮮度</span>
        <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
          以今日為準；最近交易日（應有資料日）
          <b style={{ fontFamily: HR.mono, color: HR.ink, marginLeft: 4 }}>{FD_dateLabel(fresh.expected_date)}</b>
        </span>
      </div>
      <FD_FreshRow
        label="波段常勝軍榜單" date={fresh.rank_last_date}
        pill={rankOk
          ? <Pill tone="accent" dot>已是最新</Pill>
          : <Pill tone="warn" dot>榜單尚未匯入或臨時休市</Pill>}
        note="每個交易日收盤後人工匯入當日榜單"
      />
      <FD_FreshRow
        label="自選券商 Top15" date={fresh.branches_last_date}
        pill={<Pill tone="neutral">週更</Pill>}
        note="每週一匯上週五近 20 日累計，平日落後 1–4 個交易日屬正常"
      />
      <FD_FreshRow
        label="大盤籌碼" date={fresh.market_last_date}
        pill={marketStale
          ? <Pill tone="down" dot>資料停滯</Pill>
          : <Pill tone="accent" dot>正常</Pill>}
        note={marketStale
          ? `落地檔目前停在 ${fresh.market_last_date ? FD_dateLabel(fresh.market_last_date) : '—'}${mBehind ? `，落後 ${mBehind} 個交易日` : ''}，數值僅供參考`
          : null}
      />
    </div>
  );
}

/* ─────────────  高手券商榜  ───────────── */
function FD_RankChange({ row }) {
  const ch = row.rank_change;
  if (ch === null || ch === undefined) return <span style={{ color: HR.inkFaint }}>—</span>;
  if (ch > 0) return <span style={{ color: fdBull().fg }}>▲{ch}</span>;
  if (ch < 0) return <span style={{ color: fdBear().fg }}>▼{-ch}</span>;
  return <span style={{ color: HR.inkFaint }}>持平</span>;
}

function FD_RankSection({ rank }) {
  const rows = (rank && rank.rows) || [];
  const win = (rank && rank.streak_window) || 60;
  const newcomers = (rank && rank.new_entrants) || [];
  const dropped = (rank && rank.dropped) || [];
  return (
    <Card padding="0">
      <FD_SectionHead
        title={`高手券商榜・${(rank && rank.ranking) || '波段常勝軍'}`}
        sub={rank && rank.prev_date ? `前一榜 ${FD_dateLabel(rank.prev_date)}・共 ${rows.length} 名` : `共 ${rows.length} 名`}
        sec={rank}
      />
      {rows.length === 0 ? (
        <div style={{ padding: '18px 20px', color: HR.inkFaint, fontSize: FontSizes.xs }}>
          {(rank && rank.note) || '這一天沒有榜單資料'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...FD_num_th(), width: 44 }}>名次</th>
                <th style={FD_th()}>券商</th>
                <th style={FD_num_th()}>勝率</th>
                <th style={FD_num_th()}>次數</th>
                <th style={FD_num_th()}>最大報酬</th>
                <th style={FD_num_th()}>持股數</th>
                <th style={FD_num_th()}>名次變動</th>
                <th style={FD_num_th()}>連續上榜</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.rank_pos}-${r.broker_code || r.broker_name}`}>
                  <td style={{ ...FD_num_td(), color: HR.inkSoft }}>{r.rank_pos}</td>
                  <td style={FD_td()}>
                    <span style={{ fontWeight: 500 }}>{FD_clean(r.broker_name)}</span>
                    {r.broker_code && <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginLeft: 6 }}>{r.broker_code}</span>}
                    {r.is_new && <span style={{ marginLeft: 8 }}><Pill tone="warn">新進榜</Pill></span>}
                  </td>
                  <td style={FD_num_td()}>{FD_num(r.win_rate, '%')}</td>
                  <td style={FD_num_td()}>{FD_num(r.trades)}</td>
                  <td style={FD_num_td()}>{FD_num(r.max_return, '%')}</td>
                  <td style={FD_num_td()}>{FD_num(r.stocks)}</td>
                  <td style={FD_num_td()}><FD_RankChange row={r} /></td>
                  <td style={FD_num_td()}>
                    {r.streak_days == null ? '—' : `${r.streak_days}${r.streak_days >= win ? '+' : ''} 日`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(newcomers.length > 0 || dropped.length > 0) && (
        <div style={{ padding: '12px 20px 14px', display: 'flex', flexDirection: 'column', gap: 6, borderTop: `1px solid ${HR.rule}` }}>
          {newcomers.length > 0 && (
            <div style={{ fontSize: FontSizes.xxs, color: HR.ink }}>
              <b style={{ color: fdBull().fg, marginRight: 6 }}>新進榜（{newcomers.length}）</b>
              {newcomers.map((x) => `${FD_clean(x.broker_name)} #${x.rank_pos}`).join('、')}
            </div>
          )}
          {dropped.length > 0 && (
            <div style={{ fontSize: FontSizes.xxs, color: HR.ink }}>
              <b style={{ color: fdBear().fg, marginRight: 6 }}>掉榜（{dropped.length}）</b>
              {dropped.map((x) => `${FD_clean(x.broker_name)}（前 #${x.prev_rank_pos}）`).join('、')}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/* ─────────────  自選券商 Top15  ───────────── */
const FD_POLARITY = {
  positive: { tone: 'accent', label: '順讀' },
  inverse:  { tone: 'down',   label: '反指標：買超視為賣訊' },
  unset:    { tone: 'neutral', label: '未設方向' },
};

function FD_SideTable({ title, rows, color, limit }) {
  const [open, setOpen] = React.useState(false);
  const list = open ? rows : rows.slice(0, limit);
  return (
    <div style={{ minWidth: 0 }}>
      <div style={uiStyle(
        { fontSize: FontSizes.trading_lg, fontWeight: 600, color, letterSpacing: '0.08em', margin: '2px 0 4px' },
        { fontWeight: GxT.weight.label, letterSpacing: 0 },
      )}>
        {title}（{rows.length}）
      </div>
      {rows.length === 0 ? (
        <FD_Note>無資料</FD_Note>
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...FD_num_th(), width: 24 }}>#</th>
                <th style={FD_th()}>個股</th>
                <th style={FD_num_th()}>淨張數</th>
                <th style={FD_num_th()}>淨額</th>
              </tr>
            </thead>
            <tbody>
              {list.map((x) => (
                <tr key={`${x.rank_pos}-${x.stock_code}`}>
                  <td style={{ ...FD_num_td(), color: HR.inkFaint }}>{x.rank_pos}</td>
                  <td style={{ ...FD_td(), whiteSpace: 'normal' }}>
                    <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginRight: 6 }}>{x.stock_code}</span>
                    {FD_clean(x.stock_name) || x.stock_code}
                  </td>
                  <td style={{ ...FD_num_td(), color }}>{FD_signed(x.net_lots, '張')}</td>
                  <td style={{ ...FD_num_td(), color }}>{FD_amt(x.net_amt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <FD_MoreToggle open={open} total={rows.length} shown={limit} onToggle={() => setOpen((o) => !o)} />
        </>
      )}
    </div>
  );
}

function FD_BranchCard({ item }) {
  const pol = FD_POLARITY[item.polarity] || { tone: 'neutral', label: item.polarity || '未設方向' };
  const inverse = item.polarity === 'inverse';
  return (
    <div style={uiStyle({
      border: `1px solid ${inverse ? '#E9D6D5' : HR.ruleSoft}`, borderRadius: 6,
      background: inverse ? '#FDF8F7' : HR.surface, padding: '12px 14px', minWidth: 0,
    }, {
      // 卡內子區塊：一般券商改 tonal 無框；反指標券商保留淡紅框作為辨識
      border: inverse ? '1px solid #E9D6D5' : 'none',
      background: inverse ? '#FDF8F7' : GxT.color.s1,
      borderRadius: GxT.radius.inner, padding: '14px 16px',
    })}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={uiStyle(
          { fontSize: FontSizes.sm, fontWeight: 600, color: HR.ink },
          { fontWeight: GxT.weight.title },
        )}>{FD_clean(item.branch_name) || item.branch_code}</span>
        <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{item.branch_code}</span>
        <Pill tone={pol.tone} dot>{pol.label}</Pill>
      </div>
      {item.note && <div style={{ marginBottom: 8 }}><FD_Note tone={inverse ? 'down' : undefined}>{item.note}</FD_Note></div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 18 }}>
        {/* 反指標券商：買超仍照實標「買超」，色調改用偏空（綠）提醒是賣訊 */}
        <FD_SideTable title="買超" rows={item.buy || []} color={inverse ? fdBear().fg : fdBull().fg} limit={5} />
        <FD_SideTable title="賣超" rows={item.sell || []} color={inverse ? fdBull().fg : fdBear().fg} limit={5} />
      </div>
    </div>
  );
}

function FD_BranchesSection({ branches }) {
  const items = (branches && branches.items) || [];
  const statDays = branches && branches.stat_days;
  return (
    <Card padding="0">
      <FD_SectionHead
        title="自選券商 Top15"
        sub={`${statDays ? `近 ${statDays} 日累計・` : ''}每週一匯上週五・淨額由千元換算為萬／億`}
        sec={branches} byDesign
      />
      {items.length === 0 ? (
        <div style={{ padding: '18px 20px', color: HR.inkFaint, fontSize: FontSizes.xs }}>
          {(branches && branches.note) || '這一天之前沒有自選券商資料'}
        </div>
      ) : (
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {items.map((g) => <FD_BranchCard key={g.branch_code} item={g} />)}
        </div>
      )}
    </Card>
  );
}

/* ─────────────  共同動向  ───────────── */
function FD_direction(score) {
  if (score > 0) return { label: '偏多', tone: fdBull() };
  if (score < 0) return { label: '偏空', tone: fdBear() };
  return { label: '分歧', tone: { fg: HR.inkSoft, bg: HR.ruleSoft } };
}

/* consensus 本身不帶 behind_trading_days（隨 branches 同一資料日），由頁面傳入 branches 的值 */
function FD_ConsensusSection({ consensus, behind }) {
  const rows = (consensus && consensus.rows) || [];
  const [open, setOpen] = React.useState(false);
  const LIMIT = 10;
  const list = open ? rows : rows.slice(0, LIMIT);
  const who = (a, side) => (a.branches || [])
    .filter((b) => b.side === side)
    .map((b) => FD_clean(b.branch_name) + (b.polarity === 'inverse' ? '(反)' : ''))
    .join('、');
  return (
    <Card padding="0">
      <FD_SectionHead
        title="共同動向"
        sub="同一天 ≥2 家自選券商都碰到的個股；反指標券商的買超計為賣訊"
        sec={{ ...consensus, behind_trading_days: behind }} byDesign
      />
      {rows.length === 0 ? (
        <div style={{ padding: '18px 20px', color: HR.inkFaint, fontSize: FontSizes.xs }}>沒有 2 家以上同時出現的個股</div>
      ) : (
        <div style={{ padding: '4px 0 12px' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...FD_th(), paddingLeft: 20 }}>方向</th>
                  <th style={FD_num_th()}>分數</th>
                  <th style={FD_th()}>個股</th>
                  <th style={FD_num_th()}>收盤</th>
                  <th style={FD_th()}>買方</th>
                  <th style={FD_th()}>賣方</th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => {
                  const d = FD_direction(a.score);
                  return (
                    <tr key={a.stock_code}>
                      <td style={{ ...FD_td(), paddingLeft: 20 }}>
                        <span style={uiStyle({
                          display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                          background: d.tone.bg, color: d.tone.fg, fontSize: FontSizes.trading_lg, fontWeight: 600,
                        }, { padding: '2px 10px', borderRadius: GxT.radius.chip, fontWeight: GxT.weight.label })}>{d.label}</span>
                      </td>
                      <td style={{ ...FD_num_td(), color: d.tone.fg }}>{FD_signed(a.score)}</td>
                      <td style={FD_td()}>
                        <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginRight: 6 }}>{a.stock_code}</span>
                        {FD_clean(a.stock_name)}
                      </td>
                      <td style={FD_num_td()}>{FD_num(a.close)}</td>
                      <td style={{ ...FD_td(), whiteSpace: 'normal', minWidth: 160, color: HR.inkSoft }}>{who(a, 'buy') || '—'}</td>
                      <td style={{ ...FD_td(), whiteSpace: 'normal', minWidth: 160, color: HR.inkSoft }}>{who(a, 'sell') || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '0 20px' }}>
            <FD_MoreToggle open={open} total={rows.length} shown={LIMIT} onToggle={() => setOpen((o) => !o)} />
          </div>
        </div>
      )}
    </Card>
  );
}

/* ─────────────  大盤籌碼  ───────────── */
function FD_Stat({ label, value, color, hint }) {
  return (
    <div style={uiStyle(
      { padding: '12px 14px', border: `1px solid ${HR.ruleSoft}`, borderRadius: 6, background: HR.surface },
      { padding: '14px 16px', border: 'none', borderRadius: GxT.radius.inner, background: GxT.color.s1 },
    )}>
      <div style={uiStyle(
        { fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.06em', marginBottom: 6 },
        { color: GxT.color.onV, letterSpacing: 0, fontWeight: GxT.weight.label },
      )}>{label}</div>
      <div style={uiStyle(
        { fontFamily: HR.mono, fontSize: FontSizes.md, color: color || HR.ink },
        { fontFamily: GxT.font, fontSize: 22, fontWeight: GxT.weight.title, fontVariantNumeric: 'tabular-nums' },
      )}>{value}</div>
      {hint && <div style={uiStyle(
        { fontSize: FontSizes.trading_md, color: HR.inkFaint, marginTop: 4 },
        { color: GxT.color.onV },
      )}>{hint}</div>}
    </div>
  );
}

function FD_MarketSection({ market }) {
  const g = (market && market.gov) || {};
  const net = g.net_amt;
  const behind = market && market.behind_trading_days;
  const stale = market && market.date && behind != null && behind >= 2;
  return (
    <Card padding="0">
      <FD_SectionHead title="大盤籌碼" sub="籌碼集中度與官股買賣・金額單位 億" sec={market} />
      {!market || !market.date ? (
        <div style={{ padding: '18px 20px', color: HR.inkFaint, fontSize: FontSizes.xs }}>
          {(market && market.note) || '沒有大盤籌碼資料'}
        </div>
      ) : (
        <div style={{ padding: 16 }}>
          {stale && (
            <div style={uiStyle({
              marginBottom: 12, padding: '8px 12px', borderRadius: 4,
              background: '#F4DAD8', color: HR.down, fontSize: FontSizes.xxs,
            }, { padding: '10px 14px', borderRadius: GxT.radius.inner, background: GxCore.tone('down').bg })}>
              ⚠ 大盤籌碼落地檔停在 {FD_dateLabel(market.date)}，較請求日落後 {behind} 個交易日——以下為舊資料，僅供參考。
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            <FD_Stat label="籌碼集中度" value={FD_num(market.conc)} />
            <FD_Stat label="官股買進" value={FD_num(g.gov_buy_amt, ' 億')} hint={g.buy_weight != null ? `比重 ${FD_num(g.buy_weight)}%` : null} />
            <FD_Stat label="官股賣出" value={FD_num(g.gov_sell_amt, ' 億')} hint={g.sell_weight != null ? `比重 ${FD_num(g.sell_weight)}%` : null} />
            <FD_Stat
              label="官股淨買賣" value={FD_signed(net, ' 億')}
              color={net > 0 ? fdBull().fg : net < 0 ? fdBear().fg : HR.ink}
              hint={g.net_weight != null ? `淨比重 ${FD_num(g.net_weight)}%` : null}
            />
          </div>
        </div>
      )}
    </Card>
  );
}

/* ─────────────  頁面  ───────────── */
function FendianPage() {
  // 日期清單（下拉選單）：失敗不擋主畫面，只是選單只剩當前日期
  const [dates, setDates] = React.useState([]);
  // 選定日期：'' = 最新一天（不帶 date 參數）
  const [selected, setSelected] = React.useState('');
  // 彙整：'loading' | 'ready' | 'error'
  const [phase, setPhase] = React.useState('loading');
  const [err, setErr] = React.useState(null);
  const [daily, setDaily] = React.useState(null);
  const reqSeq = React.useRef(0);

  const loadDates = React.useCallback(() => {
    let alive = true;
    api('GET', '/fendian/dates?limit=60')
      .then((d) => { if (alive) setDates((d && Array.isArray(d.rows)) ? d.rows : []); })
      .catch(() => { if (alive) setDates([]); });
    return () => { alive = false; };
  }, []);

  const loadDaily = React.useCallback((date) => {
    const seq = ++reqSeq.current;   // 只採用最後一次請求的回應，避免快速切換日期時舊回應蓋新
    setPhase('loading');
    setErr(null);
    api('GET', '/fendian/daily' + (date ? '?date=' + encodeURIComponent(date) : ''))
      .then((d) => {
        if (seq !== reqSeq.current) return;
        setDaily(d || null);
        setPhase('ready');
      })
      .catch((e) => {
        if (seq !== reqSeq.current) return;
        setErr(e || { detail: '網路連線失敗' });
        setPhase('error');
      });
  }, []);

  React.useEffect(() => loadDates(), [loadDates]);
  React.useEffect(() => { loadDaily(selected); }, [selected, loadDaily]);

  const retry = () => { loadDates(); loadDaily(selected); };
  const currentDate = selected || (daily && daily.date) || '';
  const options = dates.slice();
  if (currentDate && !options.some((r) => r.date === currentDate)) options.unshift({ date: currentDate });

  const picker = (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
      <label htmlFor="fd-date" style={uiStyle(
        { fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.08em', whiteSpace: 'nowrap' },
        { fontSize: GxT.size.label, color: GxT.color.onV, letterSpacing: 0, fontWeight: GxT.weight.label },
      )}>資料日</label>
      <select
        id="fd-date"
        value={currentDate}
        onChange={(e) => setSelected(e.target.value === (dates[0] && dates[0].date) ? '' : e.target.value)}
        style={uiStyle({
          padding: '6px 10px', borderRadius: 4, border: `1px solid ${HR.rule}`, background: HR.surface,
          color: HR.ink, fontFamily: HR.mono, fontSize: FontSizes.xxs, maxWidth: '100%',
        }, {
          // outlined field（與 primitives inputStyle 的 GX 版一致）
          minHeight: 36, padding: '6px 12px', borderRadius: GxT.radius.input,
          border: `1px solid ${GxT.color.ol}`, background: GxT.color.card,
        })}
      >
        {options.length === 0 && <option value="">最新</option>}
        {options.map((r, i) => {
          const tags = [];
          if (r.rank === false) tags.push('無榜');
          if (r.branches) tags.push('四家');
          if (r.market) tags.push('大盤');
          return (
            <option key={r.date} value={r.date}>
              {FD_dateLabel(r.date)}{i === 0 && dates.length && r.date === dates[0].date ? ' 最新' : ''}{tags.length ? '・' + tags.join('・') : ''}
            </option>
          );
        })}
      </select>
      <Button variant="ghost" size="sm" icon="refresh" onClick={retry} style={{ whiteSpace: 'nowrap' }}>重新整理</Button>
    </div>
  );

  const offline = phase === 'error' && FD_isOffline(err);

  return (
    <>
      <PageHeader
        eyebrow="每日情報 · BROKER BRANCHES"
        title="分點調查局"
        italic="前一交易日分點籌碼彙整"
        action={picker}
      />

      {phase === 'loading' && (
        <div style={uiStyle({
          height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: HR.surfaceAlt, borderRadius: 6, color: HR.inkFaint, fontSize: FontSizes.xs,
        }, { background: GxT.color.card, borderRadius: GxT.radius.card })}>
          <Spinner size={16} /> 載入分點彙整中…
        </div>
      )}

      {phase === 'error' && (
        <Card padding="0">
          {offline ? (
            <EmptyState
              icon="activity" title="收集端暫時離線"
              hint="收集端（192.168.11.37）目前無法連線，請稍後再試。"
              action={<Button variant="primary" size="sm" icon="refresh" onClick={retry}>重試</Button>}
            />
          ) : (
            <EmptyState
              icon="x" title={err && err.status === 404 ? '這一天沒有分點資料' : '分點彙整讀取失敗'}
              hint={(err && err.detail) ? String(err.detail) : '無法取得分點彙整，請重試。'}
              action={<Button variant="primary" size="sm" icon="refresh" onClick={retry}>重試</Button>}
            />
          )}
        </Card>
      )}

      {phase === 'ready' && daily && (
        <>
          <FD_FreshnessBanner fresh={daily.freshness} market={daily.market} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {daily.rank && <FD_RankSection rank={daily.rank} />}
            {daily.branches && <FD_BranchesSection branches={daily.branches} />}
            {daily.consensus && (
              <FD_ConsensusSection
                consensus={daily.consensus}
                behind={daily.branches ? daily.branches.behind_trading_days : null}
              />
            )}
            {daily.market && <FD_MarketSection market={daily.market} />}
          </div>
        </>
      )}

      <p style={{ marginTop: 18, fontSize: FontSizes.trading_lg, color: HR.inkFaint, lineHeight: 1.7 }}>
        資料來源：理財寶籌碼K線「分點調查局」本機累積，僅供內部參考，請勿轉載。
        {phase === 'ready' && daily && daily.generated_at && (
          <span style={{ fontFamily: HR.mono, marginLeft: 8 }}>產生於 {String(daily.generated_at).replace('T', ' ')}</span>
        )}
      </p>
    </>
  );
}

window.FendianPage = FendianPage;
